<script setup lang="ts">
withDefaults(
  defineProps<{
    expanded?: boolean
    // Height reserved above the panel for a notch or arrow that overhangs it. The
    // wrapper clips its content while sliding, so anything drawn outside the panel's
    // own box is cut off unless the wrapper reserves room for it and then gives that
    // room back with a negative margin.
    notchInset?: number
  }>(),
  { expanded: false, notchInset: 0 }
)

// Height is animated rather than transform-scaled so the content below moves with
// the panel instead of being overlapped by it. A height transition needs a concrete
// start and end, which `auto` is not, so each hook measures scrollHeight and pins an
// inline height, and the final hook hands the height back to the content once the
// transition has run.
function collapse(el: Element) {
  const panel = el as HTMLElement
  panel.style.height = `${panel.scrollHeight}px`
  // reading offsetHeight forces the pinned height to be applied before it changes
  // again, so the browser has two values to interpolate between rather than one
  void panel.offsetHeight
  panel.style.height = '0'
}

function expand(el: Element) {
  const panel = el as HTMLElement
  panel.style.height = `${panel.scrollHeight}px`
}

function release(el: Element) {
  ;(el as HTMLElement).style.height = ''
}
</script>

<template>
  <Transition
    @before-enter="collapse"
    @enter="expand"
    @after-enter="release"
    @before-leave="expand"
    @leave="collapse"
    @after-leave="release"
  >
    <div
      v-show="expanded"
      class="slide"
      :style="notchInset ? { paddingTop: `${notchInset}px`, marginTop: `-${notchInset}px` } : undefined"
    >
      <slot />
    </div>
  </Transition>
</template>

<style scoped>
.slide {
  box-sizing: border-box;
  overflow: hidden;
  transition: height 300ms ease-out;
}

/* Vue reads the transition duration off the element to decide when the transition
   has ended, so removing it here resolves the hooks immediately and the panel still
   releases its inline height. */
@media (prefers-reduced-motion: reduce) {
  .slide {
    transition: none;
  }
}
</style>
